import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, Users, Clock, Globe, Layers, Mic, 
  Check, ArrowRight, Star, Sparkles, Sun, Moon 
} from 'lucide-react';

const FEATURES = [
  {
    icon: Calendar,
    title: 'Meeting Agenda',
    description: 'Drag-and-drop agenda builder with time tracking and facilitator assignments.'
  },
  {
    icon: Layers,
    title: 'Forum Cards',
    description: '50+ curated conversation prompts across 5 categories to spark meaningful discussions.'
  },
  {
    icon: Globe,
    title: 'Timezone Intelligence',
    description: 'See meeting times in every member\'s timezone with travel override support.'
  },
  {
    icon: Mic,
    title: 'Voice Parking Lot',
    description: 'Capture ideas quickly with voice-to-text or typed entries during meetings.'
  },
  {
    icon: Users,
    title: 'Member Directory',
    description: 'Keep track of your forum members with profiles and contact information.'
  },
  {
    icon: Clock,
    title: 'Meeting History',
    description: 'Review past meetings, track card usage, and carry forward parking lot items.'
  },
];

const TESTIMONIALS = [
  {
    quote: "Forum Planner transformed how we run our monthly meetings. The card deck feature alone is worth it.",
    author: "Sarah M.",
    role: "Forum Chair, YPO Pacific"
  },
  {
    quote: "Finally, a tool that understands what forum groups need. The timezone feature is a lifesaver for our global forum.",
    author: "James K.",
    role: "Forum Member, EO Singapore"
  },
  {
    quote: "The voice parking lot is brilliant. We capture so many more ideas now without disrupting flow.",
    author: "Elena R.",
    role: "Forum Chair, Vistage"
  },
];

export default function LandingPage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur-lg">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary text-primary-foreground">
              <Sparkles className="w-5 h-5" />
            </div>
            <span className="font-bold text-xl">Forum Planner</span>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Link to="/login">
              <Button variant="ghost" data-testid="login-link">Login</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="get-started-btn">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-6">
            <Star className="w-3 h-3 mr-1" />
            45-day free trial • No credit card required to start
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-4xl mx-auto">
            Run Better Forum Meetings with Less Effort
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            The all-in-one meeting planner for YPO, EO, Vistage, and peer advisory forums. 
            Agenda management, conversation cards, timezone intelligence, and more.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="gap-2 text-lg px-8" data-testid="hero-cta">
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="lg" className="text-lg px-8">
                Sign In
              </Button>
            </Link>
          </div>
          
          <p className="text-sm text-muted-foreground mt-4">
            Then just $2/member/month (billed annually)
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Everything Your Forum Needs
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Built by forum members, for forum members. We know what makes great meetings.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`feature-card-${index}`}>
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20" id="pricing">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-muted-foreground text-lg">
              One plan. All features. No surprises.
            </p>
          </div>
          
          <Card className="max-w-md mx-auto shadow-xl border-primary/20" data-testid="pricing-card">
            <CardHeader className="text-center pb-2">
              <Badge className="w-fit mx-auto mb-4">Most Popular</Badge>
              <CardTitle className="text-2xl">Forum Plan</CardTitle>
              <div className="mt-4">
                <span className="text-5xl font-bold">$2</span>
                <span className="text-muted-foreground">/member/month</span>
              </div>
              <CardDescription className="mt-2">
                Billed annually (12 month minimum)
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-3">
                {[
                  '45-day free trial',
                  'Unlimited meetings',
                  '50+ forum cards included',
                  'Drag-and-drop agenda builder',
                  'Voice parking lot',
                  'Timezone intelligence',
                  'Member directory',
                  'Meeting history & analytics',
                  'Invite unlimited members',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <Link to="/register" className="block mt-8">
                <Button className="w-full" size="lg" data-testid="pricing-cta">
                  Start Your Free Trial
                </Button>
              </Link>
              
              <p className="text-center text-sm text-muted-foreground mt-4">
                No credit card required to start
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Loved by Forum Leaders
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, index) => (
              <Card key={index} className="bg-card" data-testid={`testimonial-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                    ))}
                  </div>
                  <p className="text-lg mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold">{testimonial.author}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Forum Meetings?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            Join hundreds of forum groups already using Forum Planner.
          </p>
          <Link to="/register">
            <Button size="lg" className="gap-2 text-lg px-8" data-testid="final-cta">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">Forum Planner</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 Forum Planner. All rights reserved.
            </p>
            <Link to="/admin/login" className="text-sm text-muted-foreground hover:text-foreground">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
